import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

const TIPO_COLOR_NIVEL1 = { INGRESO: "#4ade80", GASTO: "#f87171" };

function colorFila(fila) {
  if (fila.nivel === 1) return TIPO_COLOR_NIVEL1[fila.tipo] ?? "#f97316";
  if (fila.nivel === 2) return TIPO_COLOR_NIVEL1[fila.tipo] ?? "var(--text-primary)";
  return fila.nivel === 3 ? "var(--text-secondary)" : "var(--text-muted)";
}

// Separador entre secciones
function Separador({ label, color }) {
  return (
    <tr>
      <td colSpan={3} style={{ padding: "7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize: "0.78rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

// Fila de total de sección
function FilaTotal({ label, monto, color }) {
  const { fmtCRC } = useCurrency();
  return (
    <tr style={{ borderTop: `1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding: "8px 12px", paddingLeft: 26,
        fontWeight: 700, color, fontSize: "0.85rem" }}>{label}</td>
      <td className="text-end" style={{ padding: "8px 14px",
        fontFamily: "monospace", fontWeight: 800, color, fontSize: "0.88rem" }}>
        {fmtCRC(monto)}
      </td>
    </tr>
  );
}

export default function EstadoResultados() {
  const { t } = useTranslation();
  const { fmtCRC, isUSD, setMultimoneda } = useCurrency();

  // valorFila: uses USD field when toggle is active
  function valorFila(fila) {
    const s = Number(isUSD ? (fila.saldo_usd ?? 0) : (fila.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    const color = fila.tipo === "INGRESO" ? "#4ade80" : "#f87171";
    return { text: fmtCRC(s), color: fila.nivel <= 2 ? color : "var(--text-secondary)" };
  }

  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde,    setFechaDesde]    = useState(mesInicio);
  const [fechaHasta,    setFechaHasta]    = useState(mesFin);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [soloConMov,    setSoloConMov]    = useState(true);
  const [collapsed,     setCollapsed]     = useState(new Set());
  const pdfER = usePDFIndividual("/api/finance/reportes/estado-resultados-pdf/", "estado_resultados.pdf");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/estado-resultados/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, setMultimoneda]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const ingresos = filas.filter(f => f.tipo === "INGRESO" && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));
  const gastos   = filas.filter(f => f.tipo === "GASTO"   && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));

  // Use USD totals when toggle is active — amounts come directly from backend
  const tI  = Number(isUSD ? (data?.total_ingresos_usd ?? 0) : (data?.total_ingresos ?? 0));
  const tG  = Number(isUSD ? (data?.total_gastos_usd   ?? 0) : (data?.total_gastos   ?? 0));
  const res = Number(isUSD ? (data?.resultado_usd      ?? 0) : (data?.resultado      ?? 0));
  const sup = data?.es_superavit ?? true;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.estadoResultados")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.estadoResultados")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.ingresosGastos")}</p>
      </div>

      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfER.generar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              disabled={pdfER.loading || loading || !data}
              style={{ padding: "7px 14px", background: "rgba(59,130,246,0.15)",
                border: "1px solid rgba(59,130,246,0.5)", borderRadius: 6,
                color: "#60a5fa", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem",
                opacity: (pdfER.loading || !data) ? 0.5 : 1 }}>
              {pdfER.loading
                ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("reportes.generando")}</>
                : <><i className="fa-solid fa-file-pdf" style={{ marginRight: 5 }}/>{t("reportes.pdf")}</>}
            </button>
            {pdfER.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.totalIngresos"), value: tI, color: "#4ade80", icon: "fa-arrow-trend-up" },
          { label: t("reportes.totalGastos"),   value: tG, color: "#f87171", icon: "fa-arrow-trend-down" },
          { label: sup ? t("reportes.superavit") : t("reportes.deficit"),
            value: Math.abs(res), color: sup ? "#f97316" : "#a78bfa",
            icon:  sup ? "fa-circle-check" : "fa-circle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={ingresos.length + gastos.length} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 160 }}>{t("reportes.monto")}</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={3} msg={t("reportes.calculandoResultados")}/>
                : (ingresos.length + gastos.length === 0)
                  ? <FilaVacia colSpan={3} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {ingresos.length > 0 && <>
                      <Separador label={t("reportes.ingresos")} color="#4ade80"/>
                      {ingresos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila}/>)}
                      <FilaTotal label={t("reportes.totalIngresos")} monto={tI} color="#4ade80"/>
                    </>}
                    {gastos.length > 0 && <>
                      <Separador label={t("reportes.gastos")} color="#f87171"/>
                      {gastos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila}/>)}
                      <FilaTotal label={t("reportes.totalGastos")} monto={tG} color="#f87171"/>
                    </>}
                    {data && (
                      <tr style={{ background: sup ? "rgba(249,115,22,0.08)" : "rgba(167,139,250,0.08)",
                        borderTop: `3px solid ${sup ? "#f97316" : "#a78bfa"}` }}>
                        <td colSpan={2} style={{ padding: "12px 12px", fontWeight: 800, fontSize: "0.92rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          {sup ? t("reportes.superavitLabel") : t("reportes.deficitLabel")}
                        </td>
                        <td className="text-end" style={{ padding: "12px 14px",
                          fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          {fmtCRC(Math.abs(res))}
                        </td>
                      </tr>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
